import Select from 'react-select-2';
import type { VoidFunctionComponent } from 'react';

import { Action } from '../../../typings/permissions';
import {
  userHasPermissionForTeamProjects,
  userHasPermissionForTeamReleaseGroups,
  userHasPermissionToViewAnyProject,
} from '../../helpers/permissions';
import type { Team } from '../../../shared/model-typings';

const userCanAddProjectToTeam = (team: Team): boolean => userHasPermissionForTeamProjects(Action.Add, team.id);

const userCanAddReleaseGroupToTeam = (team: Team): boolean =>
  userHasPermissionForTeamReleaseGroups(Action.Add, team.id);

/**
 * teamsUserCanAddProject returns the teams to which the current user can add a project.
 * This function exists as a convenience for components that use SelectTeam;
 * they can use this function to filter their own allTeams list to decide whether to render SelectTeam.
 * @param allTeams The list of all available teams for the organization.
 */
export const teamsUserCanAddProject = (allTeams: Team[]): Team[] => allTeams.filter(userCanAddProjectToTeam);

/**
 * teamsUserCanAddReleaseGroup returns the teams to which the current user can add a Release Group.
 * This function exists as a convenience for components that use SelectTeam;
 * they can use this function to filter their own allTeams list to decide whether to render SelectTeam.
 * @param allTeams The list of all available teams for the organization.
 */
export const teamsUserCanAddReleaseGroup = (allTeams: Team[]): Team[] => allTeams.filter(userCanAddReleaseGroupToTeam);

// TeamOption represents a team that is passed to react-select
type TeamOption = {
  value: number;
  label: string;
};

/**
 * Converts a Team to a TeamOption for use with react-select.
 */
const optionForTeam = (team: Team): TeamOption => {
  return { value: team.id, label: team.name };
};
/**
 * Maps a list of Teams to a list of TeamOptions for use with react-select
 */
const optionsForTeams = (teams: Team[]): TeamOption[] => {
  return teams.map(optionForTeam);
};

/**
 * Returns the Team object associated with a given TeamOption.
 */
const teamForOption = (teams: Team[], option: TeamOption): Team | undefined => {
  return teams.find((team) => team.id === option.value);
};

/**
 * Returns the list of Teams associated with a list of TeamOptions.
 */
const teamsForOptions = (teams: Team[], options: TeamOption[]): Team[] => {
  // TS doesn't know that the filter will remove undefined values, so we need to cast it.
  return options.map((option) => teamForOption(teams, option)).filter((option) => !!option) as Team[];
};

const filterFn = (permissionKind: SelectTeamProps['permissionKind']) =>
  permissionKind === 'project' ? userCanAddProjectToTeam : userCanAddReleaseGroupToTeam;

export type SelectTeamProps = {
  onTeamsSelected: (teams: Team[]) => void;
  selectedTeams: Team[];
  allTeams: Team[];
  permissionKind: 'project' | 'releaseGroup';
};

const SelectTeam: VoidFunctionComponent<SelectTeamProps> = (props) => {
  const allTeamOptions = optionsForTeams(props.allTeams.filter(filterFn(props.permissionKind)));

  const teamSelectionChanged = (options: TeamOption[]) => {
    props.onTeamsSelected(teamsForOptions(props.allTeams, options));
  };

  return (
    <Select
      closeMenuOnSelect={false}
      isMulti
      isSearchable
      options={allTeamOptions}
      value={optionsForTeams(props.selectedTeams)}
      onChange={teamSelectionChanged}
      placeholder={`Select one or more teams${userHasPermissionToViewAnyProject() ? ' (optional)' : ''}`}
    />
  );
};

export default SelectTeam;
